import React from 'react';

import { useDispatch, useSelector } from 'react-redux';

import * as magicImportApi from 'api/magicImport';
import { IDropZoneFiles } from 'components/shared/Assets/shared/Form/types';
import { SNACKBAR_SKIN } from 'components/shared/Snackbar';
import Text from 'components/shared/Typography/Text';
import { showSnackbar } from 'hooks/useSnackbar';
import { magicImportTripEventsAction } from 'store/itinerariesItem';
import { itinerariesItemDataSelector, itineraryScheduleTypeSelector } from 'store/itinerariesItem/selectors';
import { setMovingBlocked } from 'store/movingBlocked';
import { accountIdSelector } from 'store/user/selectors';

import s from '../MagicImportButton.module.css';

const MONITORING_INTERVAL = 2_000; // 2 Sec
const MONITORING_TIMEOUT = 600_000; // 10 Min
const SNACKBAR_ID = 'MAGIC_IMPORT';
const STORAGE_KEY = 'MAGIC_IMPORT_LAST_ATTEMPT';
const STORAGE_RESTORE_THRESHOLD = MONITORING_TIMEOUT; // 5 Min;

export const useMagicImport = () => {
  const dispatch = useDispatch();
  const [status, setStatus] = React.useState<'idle' | 'uploading' | 'monitoring' | 'gettingResult'>('idle');
  const abortRef = React.useRef<AbortController | null>(null);
  const itinerary = useSelector(itinerariesItemDataSelector);
  const scheduleType = useSelector(itineraryScheduleTypeSelector);
  const accId = useSelector(accountIdSelector);

  const magicImport = async (file: IDropZoneFiles) => {
    let hideLastSnackbar: (() => void) | undefined;
    try {
      dispatch(setMovingBlocked(true));
      setStatus('uploading');
      hideLastSnackbar = showStatusSnackbar('Magic import - file uploading');
      const response = await magicImportApi.uploadFile(file);
      if (!response.id) {
        dispatch(setMovingBlocked(false));
        throw new Error('Failed to get magic import id');
      }
      saveAttemptToStorage({ proposalId: itinerary.id, importId: response.id, userId: accId });
      monitoringAndGettingResult(response.id);
    } catch (e) {
      console.error(e);
      showSnackbar({ text: 'Magic import failed to upload', uniqueId: SNACKBAR_ID + '-error' });
    } finally {
      hideLastSnackbar?.();
    }
  };

  const monitoringAndGettingResult = async (id: number) => {
    let hideLastSnackbar: (() => void) | undefined;
    try {
      dispatch(setMovingBlocked(true));
      setStatus('monitoring');
      hideLastSnackbar = showMonitoringSnackbar({
        onCancel: () => {
          clearAttemptFromStorage();
          cleanup();
        },
      });
      abortRef.current = new AbortController();
      const response = await monitorStatus(id, abortRef.current);
      if (response.status !== 'completed') throw new Error('Magic import failed after monitoring');
      hideLastSnackbar?.();
      setStatus('gettingResult');
      hideLastSnackbar = showStatusSnackbar('Magic import - getting result');
      if (!response?.data?.length) throw new Error('Failed to get magic import result');

      await dispatch(
        magicImportTripEventsAction({
          importEvents: response.data,
          itineraryOrganizedBy: scheduleType,
        }),
      );

      showFinalSnackbar({ type: 'success' });
    } catch (e) {
      console.error(e);
      hideLastSnackbar?.();
      showFinalSnackbar({ type: 'failed', onRetry: () => retryImport(id) });
    } finally {
      cleanup();
      clearAttemptFromStorage();
    }
  };

  const cleanup = () => {
    hideLastSnackbar?.();
    setStatus('idle');
    dispatch(setMovingBlocked(false));

    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  };

  const retryImport = async (id: number) => {
    try {
      await magicImportApi.retryImport(id);
      monitoringAndGettingResult(id);
    } catch (e) {
      console.error(e);
      showSnackbar({ text: 'Magic import failed to retry', uniqueId: SNACKBAR_ID + '-error' });
    }
  };

  React.useEffect(() => {
    const attempt = getLastAttempt({ userId: accId, proposalId: itinerary.id as number });
    if (attempt && Date.now() - attempt.timestamp < STORAGE_RESTORE_THRESHOLD) {
      monitoringAndGettingResult(attempt.importId);
    }

    return () => {
      cleanup();
    };
  }, []);

  return { status, magicImport };
};

let hideLastSnackbar: (() => void) | undefined;
const showStatusSnackbar = (text: string) => {
  hideLastSnackbar?.();
  hideLastSnackbar = showSnackbar({
    text,
    uniqueId: SNACKBAR_ID,
    autoClose: false,
    prohibitManualClose: true,
  });
  return hideLastSnackbar;
};

const monitorStatus = async (
  id: number,
  abortController: AbortController,
): Promise<{ data: magicImportApi.TripEvent[]; status: 'processing' | 'completed' | 'failed' }> => {
  return new Promise((resolve, reject) => {
    const interval = setInterval(async () => {
      const response = await magicImportApi.checkStatus(id, abortController);
      if (response.status === 'processing') return;

      clearInterval(interval);
      clearTimeout(timeout);

      if (response.status === 'completed') {
        resolve(response);
      } else {
        reject(response.status);
      }
    }, MONITORING_INTERVAL);

    const timeout = setTimeout(() => {
      clearInterval(interval);
      reject('timeout');
    }, MONITORING_TIMEOUT);

    abortController.signal.addEventListener('abort', () => {
      clearInterval(interval);
      clearTimeout(timeout);
    });
  });
};

const saveAttemptToStorage = (props: { proposalId: number; importId: number; userId: number }) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...props, timestamp: Date.now() }));
};

const clearAttemptFromStorage = () => {
  localStorage.removeItem(STORAGE_KEY);
};

const getLastAttempt = (props: {
  proposalId: number;
  userId: number;
}): { proposalId: number; userId: number; importId: number; timestamp: number } | null => {
  try {
    const item = localStorage.getItem(STORAGE_KEY);
    if (!item) return null;
    const res = JSON.parse(item);

    if (res.proposalId !== props.proposalId || res.userId !== props.userId) {
      return null;
    }

    return res;
  } catch {
    return null;
  }
};

const showFinalSnackbar = (props: { type: 'failed' | 'success'; onRetry?: () => void }) => {
  const state =
    props.type === 'failed'
      ? {
          text: 'Magic import failed',
          type: SNACKBAR_SKIN.ERROR,
          isRetry: true,
        }
      : {
          text: 'Magic import completed',
          type: SNACKBAR_SKIN.SUCCESS,
          isRetry: false,
          autoclose: true,
        };

  const hideSuccessSnackbar = showSnackbar({
    // @ts-ignore
    text: (
      <div className={s.snackbarWrapper}>
        <Text type="brandon" color="white">
          {state.text}.
        </Text>

        {state.isRetry && (
          <Text
            type="brandon"
            color="white"
            size="regular"
            onClick={() => {
              hideSuccessSnackbar?.();
              props.onRetry?.();
            }}
            className={s.cancelButton}
          >
            Retry
          </Text>
        )}
      </div>
    ),
    uniqueId: SNACKBAR_ID + '-result',
    autoClose: state.autoclose || false,
    className: s.snackbarWrapper,
    type: state.type,
  });
};

const showMonitoringSnackbar = (props: { onCancel: () => void }) => {
  const hideSuccessSnackbar = showSnackbar({
    // @ts-ignore
    text: (
      <div className={s.snackbarWrapper}>
        <Text type="brandon" color="white">
          Magic import in progress...
        </Text>

        <Text
          type="brandon"
          color="white"
          size="regular"
          onClick={() => {
            hideSuccessSnackbar?.();
            props.onCancel();
          }}
          className={s.cancelButton}
        >
          Cancel
        </Text>
      </div>
    ),
    uniqueId: SNACKBAR_ID + '-monitoring',
    autoClose: false,
    className: s.snackbarWrapper,
  });

  return hideSuccessSnackbar;
};
